require('./RAPortfolioCompositionTable.scss');

// @vendor
const React = require('react');
const PropTypes = require('prop-types');
const classNames = require('classnames');

// @commons
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const {
    ComparativeBarComposed,
} = require('commonsComponents/comparativeBarComposed');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');
const CurrencyHelper = require('utilities/currencyHelper');

// @utilites
const { FormattedText } = require('core/i18n').i18n;
const {
    formatPercentageWithDecimals,
} = require('../../utilities/RACurrencyHelper');
const RAAssetsHelper = require('../../utilities/RAAssetsHelper');

// @constants
const { PERCENTAGE_SYMBOL, EURO_SYMBOL } = require('constants/index');
const {
    ACTIVE_CLASS_MONEY_MARKET_ID,
    ACTIVE_CLASS_FIXED_INCOME_ID,
    ACTIVE_CLASS_EQUITY_ID,
    ACTIVE_CLASS_REAL_ASSETS_ID,
    EMPTY_ISIN,
    LIQUIDITY_SUBASSETCLASS_ID,
    RA_POTFOLIO_COMPOSITION_TABLE_DECIMAL_LENGTH,
    SECONDARY_BAR_COLOR,
} = require('../../raConstants/index');
const { ESG_PRODUCT } = require('constants/flagBasedFeatures');

class RAPortfolioCompositionTable extends React.Component {
    constructor(props) {
        super(props);

        this.timestamp = React.createRef();
        this.timestamp.current = performance.now();
    }

    getRowClass(assetClassId) {
        const customClass = classNames('ra-portfolio-composition-table__row', {
            ['ra-portfolio-composition-table__row--money-market']:
                assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID,
            ['ra-portfolio-composition-table__row--fixed-income']:
                assetClassId === ACTIVE_CLASS_FIXED_INCOME_ID,
            ['ra-portfolio-composition-table__row--equity']:
                assetClassId === ACTIVE_CLASS_EQUITY_ID,
            ['ra-portfolio-composition-table__row--real-assets']:
                assetClassId === ACTIVE_CLASS_REAL_ASSETS_ID,
        });

        return customClass;
    }

    buildTableHeader() {
        const { hideLastRow } = this.props;

        return (
            <OKTableRow className="ra-portfolio-composition-table__header-row">
                <OKTableCell className="ra-portfolio-composition-table__header-cell ra-portfolio-composition-table__header-cell--first">
                    <FormattedText
                        value="raPortfolioCompositionTable-activeSubClassLabel"
                        className="ra-portfolio-composition-table__header-content"
                    />
                </OKTableCell>
                <OKTableCell className="ra-portfolio-composition-table__header-cell ra-portfolio-composition-table__header-cell--second" />
                <OKTableCell
                    className={classNames(
                        'ra-portfolio-composition-table__header-cell',
                        !hideLastRow
                            ? 'ra-portfolio-composition-table__header-cell--third'
                            : 'ra-portfolio-composition-table__header-cell--last',
                    )}
                >
                    <FormattedText
                        value="raPortfolioCompositionTable-portfolioPercentageLabel"
                        className="ra-portfolio-composition-table__header-content"
                    />
                </OKTableCell>
                {!hideLastRow && (
                    <OKTableCell className="ra-portfolio-composition-table__header-cell ra-portfolio-composition-table__header-cell--last">
                        <FormattedText
                            value="raPortfolioCompositionTable-amountLabel"
                            className="ra-portfolio-composition-table__header-content"
                        />
                    </OKTableCell>
                )}
            </OKTableRow>
        );
    }

    buildTooltip(assetWeight) {
        const { onHoverTooltip } = this.props;
        const formattedAssetWeight = formatPercentageWithDecimals(assetWeight);
        const formattedContent = (
            <FormattedTextWithComponents
                value="raPortfolioCompositionTable-raPortfolioCompositionTable_liquidityTooltip"
                injectedComponents={[formattedAssetWeight]}
            />
        );
        return (
            <div className="ra-portfolio-composition-table__cell-tooltip">
                <OKTooltip
                    content={formattedContent}
                    offset={[10, -15]}
                    onHover={onHoverTooltip}
                />
            </div>
        );
    }

    buildLiquidityInfoRow(assetWeight) {
        const formattedAssetWeight = formatPercentageWithDecimals(assetWeight);
        const formattedContent = (
            <FormattedTextWithComponents
                value="raPortfolioCompositionTable-raPortfolioCompositionTable_liquidityTooltip"
                injectedComponents={[formattedAssetWeight]}
                className="ra-portfolio-composition-table__explanatory-text"
            />
        );
        return (
            <OKTableRow className="ra-portfolio-composition-table__row ra-portfolio-composition-table__row--liquidity-info">
                <OKTableCell
                    colSpan={7}
                    className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--first"
                >
                    {formattedContent}
                </OKTableCell>
            </OKTableRow>
        );
    }

    getIdBasedOnEpoch() {
        return this.timestamp.current++;
    }

    buildTableBody() {
        const {
            flagBasedFeatures,
            hideLastRow,
            immAssetAllocations,
            immManagementPlan,
        } = this.props;
        const tableRows = [];
        const initialContributionAmount = immManagementPlan.get(
            'initialContributionAmount',
        );
        immAssetAllocations.forEach(assetAllocation => {
            const assetClassId = assetAllocation.get('assetClassId');
            const assetClass = assetAllocation.get('assetClass');
            const rowClass = this.getRowClass(assetClassId);
            const barColor = RAAssetsHelper.getBarColor(assetClassId);
            const subAssetClasses = assetAllocation.get('subAssetClasses');
            const getSortedValue = a =>
                Number(a.get('subAssetClassId').slice(-3));
            const sortedSubAssetClasses = subAssetClasses.sort(
                (a, b) => getSortedValue(a) - getSortedValue(b),
            );
            sortedSubAssetClasses.forEach(immSubAssetClass => {
                const subAssetClass = immSubAssetClass.get('subAssetClass');
                const subAssetClassId = immSubAssetClass.get('subAssetClassId');
                const subAssetWeightPercentageNoSymbol = parseInt(
                    formatPercentageWithDecimals(
                        immSubAssetClass.get('weight'),
                        false,
                    ),
                    10,
                );
                const subAssetWeightPercentage = formatPercentageWithDecimals(
                    immSubAssetClass.get('weight'),
                );
                const assets = immSubAssetClass.get('assets');

                tableRows.push(
                    <OKTableRow
                        className={rowClass}
                        key={subAssetClassId + this.getIdBasedOnEpoch()}
                    >
                        <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--first">
                            <span className="ra-portfolio-composition-table__cell-content-first-asset ra-portfolio-composition-table__cell-content-first-asset--bold">
                                {assetClass} - {subAssetClass}
                            </span>
                        </OKTableCell>
                        <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--second">
                            <span className="ra-portfolio-composition-table__cell-content-second ra-portfolio-composition-table__cell-percentage">
                                {subAssetWeightPercentage}
                            </span>
                        </OKTableCell>
                        <OKTableCell
                            className={classNames(
                                'ra-portfolio-composition-table__cell',
                                !hideLastRow
                                    ? 'ra-portfolio-composition-table__cell--third'
                                    : 'ra-portfolio-composition-table__cell--last',
                            )}
                        >
                            <div className="ra-portfolio-composition-table__comparative-bar">
                                <ComparativeBarComposed
                                    firstValue={
                                        subAssetWeightPercentageNoSymbol
                                    }
                                    secondValue={
                                        100 - subAssetWeightPercentageNoSymbol
                                    }
                                    totalValue={100}
                                    firstColor={barColor}
                                    secondColor={SECONDARY_BAR_COLOR}
                                    height={10}
                                />
                            </div>
                        </OKTableCell>
                        {!hideLastRow && (
                            <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--last" />
                        )}
                    </OKTableRow>,
                );
                const assetsSize = assets.size;
                assets.forEach((immAsset, index) => {
                    const assetName = immAsset.get('fundName');
                    const isin = immAsset.get('isin');
                    const isESG = immAsset.get('esg', false);
                    const fundRowClass = classNames(
                        rowClass,
                        'ra-portfolio-composition-table__cell-fund-row',
                        {
                            'ra-portfolio-composition-table__cell-fund-row--last':
                                assetsSize === index + 1,
                        },
                    );
                    const assetWeight = immAsset.get('weight');
                    const assetPercentage =
                        assetsSize > 1
                            ? formatPercentageWithDecimals(assetWeight)
                            : '';
                    const amount = CurrencyHelper.formatWithoutRound(
                        assetWeight * initialContributionAmount,
                        RA_POTFOLIO_COMPOSITION_TABLE_DECIMAL_LENGTH,
                    );
                    const tooltip =
                        subAssetClassId === LIQUIDITY_SUBASSETCLASS_ID &&
                        isin === EMPTY_ISIN &&
                        !__CONTACT_CENTER__
                            ? this.buildTooltip(assetWeight)
                            : null;
                    const liquidityInfoRow =
                        subAssetClassId === LIQUIDITY_SUBASSETCLASS_ID &&
                        isin === EMPTY_ISIN &&
                        __CONTACT_CENTER__
                            ? this.buildLiquidityInfoRow(assetWeight)
                            : null;
                    const ESGTag =
                        flagBasedFeatures.get(ESG_PRODUCT) && isESG ? (
                            <i className="ra-portfolio-composition-table__cell-content-esg-icon" />
                        ) : (
                            ''
                        );
                    tableRows.push(
                        <React.Fragment
                            key={
                                subAssetClassId +
                                index +
                                this.getIdBasedOnEpoch()
                            }
                        >
                            <OKTableRow className={fundRowClass}>
                                <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--first">
                                    <span className="ra-portfolio-composition-table__cell-content-first-asset">
                                        {assetName}
                                        {ESGTag}
                                    </span>
                                    {tooltip}
                                </OKTableCell>
                                <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--second">
                                    {assetPercentage}
                                </OKTableCell>
                                <OKTableCell
                                    className={classNames(
                                        'ra-portfolio-composition-table__cell',
                                        !hideLastRow
                                            ? 'ra-portfolio-composition-table__cell--third'
                                            : 'ra-portfolio-composition-table__cell--last',
                                    )}
                                />
                                {!hideLastRow && (
                                    <OKTableCell className="ra-portfolio-composition-table__cell ra-portfolio-composition-table__cell--last">
                                        <span className="ra-portfolio-composition-table__cell-content-second ra-portfolio-composition-table__cell-percentage">
                                            {amount} {EURO_SYMBOL}
                                        </span>
                                    </OKTableCell>
                                )}
                            </OKTableRow>
                            {liquidityInfoRow}
                        </React.Fragment>,
                    );
                });
            });
        });

        return tableRows;
    }

    buildTableFooter() {
        const { immManagementPlan, hideLastRow } = this.props;
        const initialContributionAmount = immManagementPlan.get(
            'initialContributionAmount',
        );
        const totalPercentage = formatPercentageWithDecimals(1, false);
        const amount = CurrencyHelper.formatWithoutRound(
            initialContributionAmount,
            RA_POTFOLIO_COMPOSITION_TABLE_DECIMAL_LENGTH,
        );

        return (
            <OKTableRow className="ra-portfolio-composition-table__footer-row">
                <OKTableCell className="ra-portfolio-composition-table__footer-cell ra-portfolio-composition-table__footer-cell--first">
                    <FormattedText
                        value="raPortfolioCompositionTable-totalLabel"
                        className="ra-portfolio-composition-table__footer-content-first"
                    />
                </OKTableCell>
                <OKTableCell className="ra-portfolio-composition-table__footer-cell--second">
                    <span className="ra-portfolio-composition-table__footer-content-percentage">
                        {totalPercentage} {PERCENTAGE_SYMBOL}
                    </span>
                </OKTableCell>
                <OKTableCell className="ra-portfolio-composition-table__footer-cell" />
                {!hideLastRow && (
                    <OKTableCell className="ra-portfolio-composition-table__footer-cell--last">
                        <span className="ra-portfolio-composition-table__footer-content-last">
                            {amount} {EURO_SYMBOL}
                        </span>
                    </OKTableCell>
                )}
            </OKTableRow>
        );
    }

    render() {
        let tableHeader,
            tableBody,
            tableFooter = null;
        tableHeader = this.buildTableHeader();
        tableBody = this.buildTableBody();
        tableFooter = this.buildTableFooter();
        return (
            <OKTable className="ra-portfolio-composition-table">
                <thead>{tableHeader}</thead>
                <tbody>{tableBody}</tbody>
                <tfoot>{tableFooter}</tfoot>
            </OKTable>
        );
    }
}

RAPortfolioCompositionTable.propTypes = {
    immAssetAllocations: PropTypes.object.isRequired,
    immManagementPlan: PropTypes.object.isRequired,
    flagBasedFeatures: PropTypes.object.isRequired,
    onHoverTooltip: PropTypes.func,
    hideLastRow: PropTypes.boolean,
};

RAPortfolioCompositionTable.defaultProps = {
    onHoverTooltip: () => {},
    hideLastRow: false,
};

module.exports = RAPortfolioCompositionTable;
